import { Transforms } from "slate";
import { htmlToSlate } from "@slate-serializers/html";

/**
 * Plugin to handle HTML pasting using @slate-serializers/html library
 *
 * @param {Editor} editor - The Slate editor instance
 * @returns {Editor} The modified editor
 */
const withHtml = (editor) => {
  const { insertData } = editor;

  editor.insertData = (data) => {
    const html = data.getData("text/html");

    if (html) {
      try {
        // Use the library to convert HTML to Slate nodes
        const fragment = htmlToSlate(html, {
          // Configure how HTML elements map to Slate elements
          elementTags: {
            p: () => ({ type: "paragraph" }),
            div: () => ({ type: "paragraph" }),
            // Map headings to paragraphs (will be bold via textTags)
            h1: () => ({ type: "paragraph" }),
            h2: () => ({ type: "paragraph" }),
            h3: () => ({ type: "paragraph" }),
            h4: () => ({ type: "paragraph" }),
            h5: () => ({ type: "paragraph" }),
            h6: () => ({ type: "paragraph" }),
            // Lists
            ul: () => ({ type: "bulleted-list" }),
            ol: () => ({ type: "numbered-list" }),
            li: () => ({ type: "list-item" }),
            // Blockquotes as paragraphs
            blockquote: () => ({ type: "paragraph" }),
            // Code blocks as paragraphs (preserve content)
            pre: () => ({ type: "paragraph" }),
            code: () => ({ type: "paragraph" }),
          },
          // Configure how inline HTML tags map to Slate text marks
          textTags: {
            strong: () => ({ bold: true }),
            b: () => ({ bold: true }),
            em: () => ({ italic: true }),
            i: () => ({ italic: true }),
            u: () => ({ underline: true }),
            // Make heading content bold
            h1: () => ({ bold: true }),
            h2: () => ({ bold: true }),
            h3: () => ({ bold: true }),
            h4: () => ({ bold: true }),
            h5: () => ({ bold: true }),
            h6: () => ({ bold: true }),
          },
          // Handle inline styles (Google Docs, etc.)
          elementStyleMap: {
            "font-weight": (value) => {
              const weight = value.toLowerCase();
              // Only treat as bold if weight is 600+ or explicitly "bold"
              if (weight === "bold" || (parseInt(weight) >= 600 && weight !== "400" && weight !== "normal")) {
                return { bold: true };
              }
              return {};
            },
            "font-style": (value) => {
              if (value === "italic") {
                return { italic: true };
              }
              return {};
            },
            "text-decoration": (value) => {
              if (value.includes("underline")) {
                return { underline: true };
              }
              return {};
            },
            "text-decoration-line": (value) => {
              if (value.includes("underline")) {
                return { underline: true };
              }
              return {};
            },
          },
        });

        if (fragment && Array.isArray(fragment) && fragment.length > 0) {
          // Verify fragment has actual content
          const hasContent = fragment.some((node) => {
            if (node.type) return true;
            if (node.text && node.text !== "") return true;
            return false;
          });

          if (hasContent) {
            Transforms.insertFragment(editor, fragment);
            return;
          }
        }
      } catch (error) {
        // If HTML parsing fails, fall back to plain text/markdown
      }
    }

    // Fallback to default behavior (plain text or markdown plugin)
    insertData(data);
  };

  return editor;
};

export default withHtml;
